const BRIEF_FIELD_DESCRIPTIONS = {
    targetAudience: 'Target audience for the ad (e.g., young adults, parents, professionals)',
    platforms: 'Platforms to run the ad on (e.g., TikTok, Instagram, Meta, YouTube)',
    campaignGoal: 'Campaign goal (e.g., brand awareness, conversions, engagement)',
    hook: 'Opening hook for the video ad - first 3 seconds to grab attention',
    keyMessage: 'Core message or value proposition to communicate',
    tone: 'Tone or style of the ad (e.g., energetic, emotional, humorous)',
    cta: 'Call to action (e.g., "Shop now", "Learn more")',
    constraints: 'Any constraints or special requirements',
};
const buildBriefProperties = (fields) => {
    const properties = {};
    for (const field of fields) {
        const description = BRIEF_FIELD_DESCRIPTIONS[field];
        if (field === 'platforms') {
            properties[field] = {
                type: 'array',
                items: { type: 'string' },
                description,
            };
        }
        else {
            properties[field] = { type: 'string', description };
        }
    }
    return properties;
};
const ASK_USER_RECOMMENDATIONS_SCHEMA = {
    type: 'object',
    description: 'Model-generated ask_user recommendations for the required brief fields. Generate exactly four useful options for each field, tailored to the user request and written in the user/system language.',
    properties: {
        targetAudience: {
            type: 'array',
            description: 'Exactly four target-audience recommendations for this specific ad campaign or ad video.',
            items: {
                type: 'object',
                properties: {
                    label: {
                        type: 'string',
                        description: 'Short user-facing option label in the user language.',
                    },
                    hint: {
                        type: 'string',
                        description: 'One short explanatory line in the user language explaining the angle.',
                    },
                    value: {
                        type: 'string',
                        description: 'Complete brief value to submit when selected, in the user language.',
                    },
                },
                required: ['label', 'hint', 'value'],
            },
        },
        platforms: {
            type: 'array',
            description: 'Exactly four platform or channel mix recommendations for this specific ad campaign or ad video.',
            items: {
                type: 'object',
                properties: {
                    label: {
                        type: 'string',
                        description: 'Short user-facing option label in the user language.',
                    },
                    hint: {
                        type: 'string',
                        description: 'One short explanatory line in the user language explaining the format fit.',
                    },
                    value: {
                        type: 'string',
                        description: 'Complete brief value to submit when selected, in the user language.',
                    },
                },
                required: ['label', 'hint', 'value'],
            },
        },
        campaignGoal: {
            type: 'array',
            description: 'Exactly four campaign-goal recommendations for this specific ad campaign or ad video.',
            items: {
                type: 'object',
                properties: {
                    label: {
                        type: 'string',
                        description: 'Short user-facing option label in the user language.',
                    },
                    hint: {
                        type: 'string',
                        description: 'One short explanatory line in the user language explaining the objective.',
                    },
                    value: {
                        type: 'string',
                        description: 'Complete brief value to submit when selected, in the user language.',
                    },
                },
                required: ['label', 'hint', 'value'],
            },
        },
    },
};
const VIDEO_BRIEF_FIELDS = [
    'targetAudience',
    'platforms',
    'campaignGoal',
    'hook',
    'keyMessage',
    'tone',
    'cta',
    'constraints',
];
const CAMPAIGN_BRIEF_FIELDS = [
    'targetAudience',
    'platforms',
    'campaignGoal',
    'keyMessage',
    'tone',
    'cta',
    'constraints',
];
export const RECOGNIZE_IMAGE_TOOL = {
    type: 'function',
    function: {
        name: 'recognize_image',
        description: 'Call this when the user asks about an attached image or when visual details are needed before answering. The client will run image recognition and return observations.',
        parameters: {
            type: 'object',
            properties: {
                imageIndex: {
                    type: 'number',
                    description: '1-based index of the attached image to inspect. Omit to inspect every attached image.',
                },
                prompt: {
                    type: 'string',
                    description: 'Optional recognition focus, such as product details, visible text, style, or composition.',
                },
            },
            required: [],
        },
    },
};
export const RECOGNIZE_VIDEO_TOOL = {
    type: 'function',
    function: {
        name: 'recognize_video',
        description: 'Call this when the user asks about an attached video or when video content details are needed before answering. The client will run video recognition and return observations.',
        parameters: {
            type: 'object',
            properties: {
                videoIndex: {
                    type: 'number',
                    description: '1-based index of the attached video to inspect. Omit to inspect every attached video.',
                },
                prompt: {
                    type: 'string',
                    description: 'Optional recognition focus, such as subjects, actions, scene, product details, or mood.',
                },
            },
            required: [],
        },
    },
};
export const UPDATE_IMAGE_TOOL = {
    type: 'function',
    function: {
        name: 'update_image',
        description: 'Call this when the user asks to create a new image by editing, transforming, restyling, or generating from an attached image. The client will use the selected attached image as the reference and return generated image assets.',
        parameters: {
            type: 'object',
            properties: {
                imageIndex: {
                    type: 'number',
                    description: '1-based index of the attached image to use as the source. Omit to use the first attached image.',
                },
                prompt: {
                    type: 'string',
                    description: 'Required image update instruction from the user, including what to change and what to preserve.',
                },
            },
            required: ['prompt'],
        },
    },
};
export const ASK_USER_TOOL = {
    type: 'function',
    function: {
        name: 'ask_user',
        description: 'Open an interactive ask-user UI to collect required campaign production details. Use flowKind "ad_video" when the user wants video assets such as a video ad, short-form spot, Reel, TikTok, YouTube Short, motion creative, duration, hook, shots, camera movement, voiceover, or Chinese terms like 视频, 短视频, 成片, 口播, 拍一条, 生成视频. Use flowKind "ad_campaign" when the user wants static image assets or a general campaign brief without clear video intent, such as campaign concepts, ads, product images, posters, banners, social creatives, KV, or Chinese terms like 广告图, 海报, 主图, 静态素材. Extract any details they provided into the arguments.',
        parameters: {
            type: 'object',
            properties: {
                flowKind: {
                    type: 'string',
                    enum: ['ad_campaign', 'ad_video'],
                    description: 'Which production flow the ask-user UI should collect details for.',
                },
                ...buildBriefProperties(VIDEO_BRIEF_FIELDS),
                recommendations: ASK_USER_RECOMMENDATIONS_SCHEMA,
            },
            required: ['flowKind'],
        },
    },
};
export const EXTRACT_AD_CAMPAIGN_BRIEF_TOOL = {
    type: 'function',
    function: {
        name: 'extract_ad_campaign_brief',
        description: 'Extract a partial static/image ad campaign brief from the user message and any media analysis. Use only fields that are clearly stated or strongly implied.',
        parameters: {
            type: 'object',
            properties: buildBriefProperties(CAMPAIGN_BRIEF_FIELDS),
            required: [],
        },
    },
};
export const AGENT_CHAT_TOOLS = [
    RECOGNIZE_IMAGE_TOOL,
    RECOGNIZE_VIDEO_TOOL,
    UPDATE_IMAGE_TOOL,
];
//# sourceMappingURL=agent-chat-tools.js.map